import {
  HEADER_CONFIRMATION_CONFIDENCE,
  mappingFromHeaderCandidate,
  scoreHeaderRow,
  type FieldKey,
  type SheetMapping,
} from '../../domain';
import type { ParsedWorkbookMeta, ParsedSheetMeta } from '../inspection/parser';

export interface SheetMappingState {
  readonly selected: boolean;
  readonly mapping: SheetMapping;
}

export function createMappingStates(meta: ParsedWorkbookMeta): readonly SheetMappingState[] {
  return meta.sheets.map((sheet) => {
    const candidate = sheet.headerCandidate;
    return {
      selected: candidate !== null,
      mapping: {
        sheetName: sheet.name,
        headerRow: candidate?.rowNumber ?? 1,
        fields: candidate === null ? {} : mappingFromHeaderCandidate(candidate),
        confidence: candidate?.confidence ?? 0,
        confirmed: false,
      },
    };
  });
}

export function updateHeaderRow(
  state: SheetMappingState,
  sheet: ParsedSheetMeta,
  headerRow: number,
): SheetMappingState {
  const row = sheet.scanRows[headerRow - 1] ?? [];
  const candidate = scoreHeaderRow(row, headerRow);
  return {
    ...state,
    mapping: {
      ...state.mapping,
      headerRow,
      fields: mappingFromHeaderCandidate(candidate),
      confidence: candidate.confidence,
      confirmed: false,
    },
  };
}

export function updateFieldMapping(
  state: SheetMappingState,
  field: FieldKey,
  column: number | null,
): SheetMappingState {
  const entries = (Object.entries(state.mapping.fields) as [FieldKey, number][]).filter(
    ([otherField, otherColumn]) =>
      otherField !== field && (column === null || otherColumn !== column),
  );
  if (column !== null) entries.push([field, column]);
  const fields = Object.fromEntries(entries) as Partial<Record<FieldKey, number>>;
  return {
    ...state,
    mapping: { ...state.mapping, fields, confirmed: false },
  };
}

export function mappingNeedsAttention(mapping: SheetMapping): boolean {
  return mapping.confidence < HEADER_CONFIRMATION_CONFIDENCE;
}

export function columnName(column: number): string {
  let value = column;
  let label = '';
  while (value > 0) {
    value -= 1;
    label = String.fromCharCode(65 + (value % 26)) + label;
    value = Math.floor(value / 26);
  }
  return label;
}
